import type { DxfEntityKey } from '@frontend/api';
import {
  type Aabb2,
  type Loop,
  type Point2,
  polygonContainsPoint,
} from '@frontend/viewer-core';

import type { DrawingModel, MaterialFillRing } from './types';

/** A loop's boundary, without the duplicate point closing the ring. */
const ringOf = (loop: Loop): readonly Point2[] =>
  loop.points.length > 1 ? loop.points.slice(0, -1) : loop.points;

const aabbsOverlap = (a: Aabb2, b: Aabb2): boolean =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

const aabbArea = ([minX, minY, maxX, maxY]: Aabb2): number =>
  Math.max(0, maxX - minX) * Math.max(0, maxY - minY);

/** Average of ring vertices — nesting probe, not a true centroid of area. */
const ringAnchor = (ring: readonly Point2[]): Point2 => {
  let sumX = 0;
  let sumY = 0;
  ring.forEach(([x, y]) => {
    sumX += x;
    sumY += y;
  });
  return [sumX / ring.length, sumY / ring.length];
};

/**
 * True when `inner` is nested inside `outer`.
 *
 * Bounding boxes only cull non-overlapping pairs. When boxes overlap, a
 * point-in-polygon test on an anchor inside `inner` decides containment —
 * AABB containment alone would mis-nest disjoint loops whose boxes nest
 * (a loop sitting in the mouth of a C-shaped outer, for example).
 */
const loopContainsLoop = (outer: Loop, inner: Loop): boolean => {
  if (!aabbsOverlap(outer.bounds, inner.bounds)) {
    return false;
  }
  return polygonContainsPoint(ringOf(outer), ringAnchor(ringOf(inner)));
};

/** Signed area of the triangle `a,b,c`; sign says which side `c` is on. */
const orientation = (a: Point2, b: Point2, c: Point2): number =>
  (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

/**
 * True only for a proper interior crossing of segments `p1p2` and `p3p4` —
 * a shared endpoint or a tangent touch does not count, matching the spec's
 * `segmentsCross` (proper crossings only).
 */
const segmentsProperlyCross = (
  p1: Point2,
  p2: Point2,
  p3: Point2,
  p4: Point2,
): boolean => {
  const d1 = orientation(p3, p4, p1);
  const d2 = orientation(p3, p4, p2);
  const d3 = orientation(p1, p2, p3);
  const d4 = orientation(p1, p2, p4);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
};

/** True when any edge of `a` properly crosses any edge of `b`. */
const loopsProperlyCross = (a: Loop, b: Loop): boolean => {
  const ringA = ringOf(a);
  const ringB = ringOf(b);
  for (let i = 0; i < ringA.length; i += 1) {
    const a1 = ringA[i];
    const a2 = ringA[(i + 1) % ringA.length];
    for (let j = 0; j < ringB.length; j += 1) {
      const b1 = ringB[j];
      const b2 = ringB[(j + 1) % ringB.length];
      if (segmentsProperlyCross(a1, a2, b1, b2)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Every pair of `loops` whose boxes overlap, via sweep-and-prune rather than
 * a brute-force all-pairs scan.
 *
 * Sorted once by box min-X, then swept: for a given loop, every other loop
 * that could possibly share its box's X-range sits in a contiguous run right
 * after it in that sorted order (nothing further out can start early enough
 * to reach back), so the inner scan stops the moment it passes that range
 * instead of visiting the rest of the array. A drawing that is mostly
 * disjoint holes — the case that actually gets large — keeps each loop's
 * inner scan short regardless of how many thousands of *other*, distant
 * loops exist; only genuinely nearby pairs are ever produced. Both the
 * crossing test and the containment test below need exactly this candidate
 * set, since either relation implies the boxes overlap, so one sweep serves
 * both instead of two separate O(n²) passes.
 */
const overlappingPairs = function* (
  loops: readonly Loop[],
): Generator<readonly [Loop, Loop]> {
  const byMinX = [...loops].sort((a, b) => a.bounds[0] - b.bounds[0]);
  for (let i = 0; i < byMinX.length; i += 1) {
    const a = byMinX[i];
    for (let j = i + 1; j < byMinX.length; j += 1) {
      const b = byMinX[j];
      if (b.bounds[0] > a.bounds[2]) {
        // Sorted by min-X, so every loop from here on starts even later on X
        // than `a` ends — none of them can overlap `a` either.
        break;
      }
      if (aabbsOverlap(a.bounds, b.bounds)) {
        yield [a, b];
      }
    }
  }
};

/**
 * Even-odd material-fill rings: the translucent solid-sheet-vs-cutout hint
 * painted under the role overlay.
 *
 * What counts as a ring is deliberately independent of any outer/inner role
 * split — this drawing has none, and fill parity does not need one either:
 * only closed, visible, entirely-cutting loops with real area are candidates.
 * A pair that properly crosses is dropped from the fill (their enclosed region
 * is undefined) without suppressing every other part in the drawing — a single
 * decorative clash must not blank the wash on an unrelated sheet.
 *
 * Nesting is decided by a point-in-polygon test when bounding boxes overlap —
 * bbox containment alone is not enough. Depth alternates solid and hole with each level of nesting: depth 0 is a
 * top-level sheet (or an island reappearing inside a hole at depth 2, 4, …),
 * and each odd depth is cut into its nearest even ancestor as one of its holes.
 */
export const buildMaterialFillRings = (
  model: DrawingModel,
  cuttingKeys: ReadonlySet<DxfEntityKey>,
  hiddenLayers: ReadonlySet<number>,
): readonly MaterialFillRing[] | null => {
  /** A fill ring must bound area — a collapsed centreline welded into an
   *  `isClosed` loop (2 points, zero area) was aborting the whole wash via a
   *  false "crossing" against real contours. */
  const MIN_FILL_RING_POINTS = 3;
  const MIN_FILL_AREA = 1e-6;

  const cutLoops = model.loops.filter(
    (loop) =>
      loop.isClosed &&
      loop.chunks.length > 0 &&
      ringOf(loop).length >= MIN_FILL_RING_POINTS &&
      loop.area !== null &&
      Math.abs(loop.area) > MIN_FILL_AREA &&
      loop.chunks.every(
        (chunk) =>
          cuttingKeys.has(chunk.entityId) && !hiddenLayers.has(chunk.layer),
      ),
  );

  if (cutLoops.length === 0) {
    return null;
  }

  // Immediate parent = the tightest (smallest-box-area) other cut loop that
  // contains this one. Crossing pairs are dropped *locally*: a multi-part
  // drawing must not lose every sheet because one hex decoration crosses a
  // triangle elsewhere.
  const excluded = new Set<Loop>();
  for (const [a, b] of overlappingPairs(cutLoops)) {
    if (loopsProperlyCross(a, b)) {
      excluded.add(a);
      excluded.add(b);
    }
  }

  const eligible = cutLoops.filter((loop) => !excluded.has(loop));
  if (eligible.length === 0) {
    return null;
  }

  const parentOf = new Map<Loop, Loop | null>(
    eligible.map((loop) => [loop, null]),
  );
  const parentArea = new Map<Loop, number>();

  const considerParent = (child: Loop, candidate: Loop) => {
    const candidateArea = aabbArea(candidate.bounds);
    const currentArea = parentArea.get(child);
    if (currentArea === undefined || candidateArea < currentArea) {
      parentOf.set(child, candidate);
      parentArea.set(child, candidateArea);
    }
  };

  for (const [a, b] of overlappingPairs(eligible)) {
    if (loopContainsLoop(a, b)) {
      considerParent(b, a);
    } else if (loopContainsLoop(b, a)) {
      considerParent(a, b);
    }
  }

  const depthOf = (loop: Loop): number => {
    let depth = 0;
    let current = parentOf.get(loop) ?? null;
    const visited = new Set<Loop>();
    while (current) {
      if (visited.has(current)) {
        break;
      }
      visited.add(current);
      depth += 1;
      current = parentOf.get(current) ?? null;
    }
    return depth;
  };

  const directChildrenOf = new Map<Loop, Loop[]>();
  eligible.forEach((loop) => {
    const parent = parentOf.get(loop) ?? null;
    if (!parent) {
      return;
    }
    const children = directChildrenOf.get(parent);
    if (children) {
      children.push(loop);
    } else {
      directChildrenOf.set(parent, [loop]);
    }
  });

  const rings = eligible
    .filter((loop) => depthOf(loop) % 2 === 0)
    .map((solid) => ({
      outer: ringOf(solid),
      holes: (directChildrenOf.get(solid) ?? []).map(ringOf),
    }));

  return rings.length > 0 ? rings : null;
};

type CacheEntry = {
  model: DrawingModel;
  cuttingKeys: ReadonlySet<DxfEntityKey>;
  hiddenLayers: ReadonlySet<number>;
  rings: readonly MaterialFillRing[] | null;
};

/**
 * Wraps `buildMaterialFillRings` with a one-entry cache keyed by reference,
 * so an edit that never touches the cutting role skips the geometry entirely
 * instead of re-deriving it for nothing.
 *
 * This is the other half of the fix alongside `overlappingPairs`: that one
 * makes an *actual* recompute cheap; this one avoids recomputing at all when
 * `cuttingKeys` is the exact `Set` `roleIndex()` handed back last time — which
 * it does keep handing back, unchanged, for every edit that assigns some
 * other role. Most edits are exactly that.
 */
export const createMaterialFillBuilder = () => {
  let cache: CacheEntry | null = null;

  return (
    model: DrawingModel | null,
    cuttingKeys: ReadonlySet<DxfEntityKey>,
    hiddenLayers: ReadonlySet<number>,
  ): readonly MaterialFillRing[] | null => {
    if (!model) {
      cache = null;
      return null;
    }
    if (
      cache &&
      cache.model === model &&
      cache.cuttingKeys === cuttingKeys &&
      cache.hiddenLayers === hiddenLayers
    ) {
      return cache.rings;
    }

    const rings = buildMaterialFillRings(model, cuttingKeys, hiddenLayers);
    cache = { model, cuttingKeys, hiddenLayers, rings };
    return rings;
  };
};
